import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import Swal from "sweetalert2";
import { confirmAlert, errorAlert, successAlert } from "../../utils/alert";
import { AuthContext } from "../../context/AuthContext";
import Modal from "../../components/common/Modal";

const initialForm = {
  name: "",
  email: "",
  mobile: "",
  password: "",
  role: "district",
  district: "",
  district_id: "",
  photo: null,
};

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  return String(value || "").toLowerCase() === "true" || String(value || "") === "1";
};

const DistrictRegistration = () => {
  const { role } = useContext(AuthContext);
  const isAdmin = String(role || "").toLowerCase() === "admin";
  const [form, setForm] = useState(initialForm);
  const [users, setUsers] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [districtLogoFiles, setDistrictLogoFiles] = useState({});
  const [districtLogoUploading, setDistrictLogoUploading] = useState({});
  const [existingPhoto, setExistingPhoto] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [inchargeAssignments, setInchargeAssignments] = useState({});

  const roleOptions = useMemo(
    () => [
      { value: "district", label: "District" },
      { value: "secretary", label: "Secretary" },
      ...(isAdmin ? [{ value: "admin", label: "Admin" }] : []),
    ],
    [isAdmin]
  );

  const handleCloseModal = () => {
    setIsFormModalOpen(false);
    setEditingId(null);
    setForm(initialForm);
    setExistingPhoto(null);
  };

  const fetchDistrictOptions = useCallback(async () => {
    try {
      const rows = await API.fetchDistrictOptions();
      const list = Array.isArray(rows) ? rows : [];
      setDistrictOptions(list);
      setInchargeAssignments(Object.fromEntries(list.map((d) => [d.id, (d.managed_district_ids || []).map(Number)])));
    } catch {
      setDistrictOptions([]);
    }
  }, []);

  const saveInchargeAssignments = async (district) => {
    setRowBusy((prev) => ({ ...prev, [`incharge-${district.id}`]: true }));
    try {
      await API.updateDistrictInchargeAssignments(district.id, inchargeAssignments[district.id] || []);
      successAlert("Incharge updated", `${district.name} can now manage the selected districts.`);
      await fetchDistrictOptions();
    } catch (err) {
      errorAlert("Update failed", err?.message || "Failed to save district incharge assignments.");
    } finally {
      setRowBusy((prev) => ({ ...prev, [`incharge-${district.id}`]: false }));
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await API.fetchAdminUsers();
      const rows = Array.isArray(res) ? res : [];
      setUsers(isAdmin ? rows : rows.filter((user) => String(user?.role || "").toLowerCase() !== "admin"));
    } catch (err) {
      setUsers([]);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchUsers();
    fetchDistrictOptions();
  }, [fetchDistrictOptions, fetchUsers]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "photo") {
      setForm((prev) => ({ ...prev, photo: files?.[0] || null }));
      return;
    }
    if (name === "mobile") {
      setForm((prev) => ({ ...prev, mobile: String(value || "").replace(/\D/g, "").slice(0, 15) }));
      return;
    }
    if (name === "role" && value !== "district") {
      setForm((prev) => ({ ...prev, role: value, district: "", district_id: "" }));
      return;
    }
    if (name === "district_id") {
      const selected = districtOptions.find((d) => {
        const optionValue = d.id ? `id:${d.id}` : `name:${d.name}`;
        return optionValue === value;
      });
      setForm((prev) => ({
        ...prev,
        district_id: value,
        district: selected?.name || "",
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("mobile", form.mobile || "");
      fd.append("role", form.role);
      fd.append("district", form.district || "");
      if (String(form.district_id || "").startsWith("id:")) {
        fd.append("district_id", String(form.district_id).replace("id:", ""));
      }
      if (!editingId && form.password) {
        fd.append("password", form.password);
      }
      if (form.photo) fd.append("photo", form.photo);

      if (editingId) {
        await API.updateAdminUser(editingId, fd);
        successAlert("User updated", "User updated successfully.");
      } else {
        await API.createAdminUser(fd);
        successAlert("User created", "User created successfully.");
      }
      setForm(initialForm);
      setEditingId(null);
      setIsFormModalOpen(false);
      await fetchUsers();
    } catch (err) {
      const resErrors = err?.errors ? Object.values(err.errors).flat().join(", ") : null;
      errorAlert("Save failed", resErrors || err?.message || "Failed to save user.");
    } finally {
      setLoading(false);
    }
  };

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return users;
    return [...users].sort((a, b) => {
      let aVal = a[sortConfig.key] ?? "";
      let bVal = b[sortConfig.key] ?? "";
      const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [users, sortConfig]);

  const handleEdit = (user) => {
    setEditingId(user.id);
    const nextRole = String(user.role || "district").toLowerCase();
    setForm({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || "",
      password: "",
      role: isAdmin || nextRole !== "admin" ? nextRole : "district",
      district: user.district || "",
      district_id: user.district_id
        ? `id:${user.district_id}`
        : (user.district ? `name:${user.district}` : ""),
      photo: null,
    });
    setExistingPhoto(user.photo || null);
    setIsFormModalOpen(true);
  };

  const handleResetPassword = async (user) => {
    const result = await Swal.fire({
      title: `Reset password for ${user.name}?`,
      input: "password",
      inputLabel: "Enter new password",
      inputPlaceholder: "New password (min 6 chars)",
      inputAttributes: { autocapitalize: "off", autocorrect: "off" },
      showCancelButton: true,
      confirmButtonText: "Reset",
      inputValidator: (value) => {
        if (!value || value.length < 6) return "Password must be at least 6 characters";
        return null;
      },
    });

    if (!result.isConfirmed) return;

    try {
      await API.resetAdminUserPassword(user.id, result.value);
      successAlert("Password reset", "User password updated successfully");
    } catch (err) {
      errorAlert("Reset failed", err?.message || "Failed to reset password");
    }
  };

  const handleDistrictBlockToggle = async (user) => {
    const userRole = String(user?.role || "").toLowerCase();
    if (userRole !== "district") {
      errorAlert("Not allowed", "Only district logins can be blocked from this panel.");
      return;
    }

    const currentlyBlocked = toBool(user?.is_blocked);
    const shouldBlock = !currentlyBlocked;

    let reason = "";
    if (shouldBlock) {
      const blockResult = await Swal.fire({
        title: `Block ${user.name}?`,
        text: "This district login will be unable to sign in.",
        input: "text",
        inputLabel: "Reason (optional)",
        inputPlaceholder: "Enter reason for blocking",
        showCancelButton: true,
        confirmButtonText: "Block",
        confirmButtonColor: "#dc2626",
      });

      if (!blockResult.isConfirmed) return;
      reason = String(blockResult.value || "").trim();
    } else {
      const { isConfirmed } = await confirmAlert({
        title: `Unblock ${user.name}?`,
        text: "This district login will be able to sign in again.",
        confirmText: "Unblock",
      });
      if (!isConfirmed) return;
    }

    const busyKey = `block-${user.id}`;
    try {
      setRowBusy((prev) => ({ ...prev, [busyKey]: true }));
      await API.updateDistrictUserBlockStatus(user.id, shouldBlock, reason);
      await fetchUsers();
      successAlert(
        shouldBlock ? "District blocked" : "District unblocked",
        shouldBlock ? "District login has been blocked successfully." : "District login has been unblocked successfully."
      );
    } catch (err) {
      errorAlert("Action failed", err?.message || "Failed to update district login status.");
    } finally {
      setRowBusy((prev) => ({ ...prev, [busyKey]: false }));
    }
  };

  const handleDistrictDelete = async (user) => {
    const userRole = String(user?.role || "").toLowerCase();
    if (userRole !== "district") {
      errorAlert("Not allowed", "Only district logins can be deleted from this panel.");
      return;
    }

    const { isConfirmed } = await confirmAlert({
      title: "Delete district login?",
      text: `${user.name} (${user.email}) will be permanently removed.`,
      confirmText: "Delete",
    });
    if (!isConfirmed) return;

    const busyKey = `delete-${user.id}`;
    try {
      setRowBusy((prev) => ({ ...prev, [busyKey]: true }));
      await API.deleteDistrictUser(user.id);
      await fetchUsers();
      successAlert("District deleted", "District login deleted successfully.");
    } catch (err) {
      errorAlert("Delete failed", err?.message || "Failed to delete district login.");
    } finally {
      setRowBusy((prev) => ({ ...prev, [busyKey]: false }));
    }
  };

  const handleDistrictLogoFileChange = (districtId, file) => {
    setDistrictLogoFiles((prev) => ({ ...prev, [districtId]: file || null }));
  };

  const handleUploadDistrictLogo = async (district) => {
    const districtId = Number(district?.id || 0);
    if (!districtId) {
      errorAlert("Invalid district", "District ID is missing");
      return;
    }

    const selectedFile = districtLogoFiles[districtId];
    if (!selectedFile) {
      errorAlert("No file selected", `Please choose a logo for ${district.name}`);
      return;
    }

    try {
      setDistrictLogoUploading((prev) => ({ ...prev, [districtId]: true }));
      const fd = new FormData();
      fd.append("logo", selectedFile);
      await API.updateDistrictLogo(districtId, fd);
      await fetchDistrictOptions();
      setDistrictLogoFiles((prev) => ({ ...prev, [districtId]: null }));
      successAlert("Logo updated", `${district.name} logo saved successfully`);
    } catch (err) {
      errorAlert("Upload failed", err?.message || "Failed to upload district logo");
    } finally {
      setDistrictLogoUploading((prev) => ({ ...prev, [districtId]: false }));
    }
  };

  const handleRemoveDistrictLogo = async (district) => {
    const districtId = Number(district?.id || 0);
    if (!districtId) return;

    const { isConfirmed } = await confirmAlert({
      title: "Remove Logo?",
      text: `Are you sure you want to remove the logo for ${district.name}?`,
      confirmText: "Remove",
      confirmButtonColor: "#dc2626",
    });

    if (!isConfirmed) return;

    try {
      setDistrictLogoUploading((prev) => ({ ...prev, [districtId]: true }));
      await API.deleteDistrictLogo(districtId);
      await fetchDistrictOptions();
      successAlert("Logo removed", `Logo for ${district.name} removed successfully.`);
    } catch (err) {
      errorAlert("Removal failed", err?.message || "Failed to remove logo.");
    } finally {
      setDistrictLogoUploading((prev) => ({ ...prev, [districtId]: false }));
    }
  };

  const handleEditDistrictName = async (district) => {
    const districtId = Number(district?.id || 0);
    if (!districtId) return;

    const result = await Swal.fire({
      title: `Rename ${district.name}`,
      input: "text",
      inputLabel: "New District Name",
      inputValue: district.name,
      inputPlaceholder: "Enter district name",
      showCancelButton: true,
      confirmButtonText: "Save",
      inputValidator: (value) => {
        if (!value || value.trim() === "") return "District name cannot be empty";
        return null;
      },
    });

    if (!result.isConfirmed) return;

    try {
      setRowBusy((prev) => ({ ...prev, [`district-name-${districtId}`]: true }));
      await API.updateDistrictName(districtId, result.value.trim());
      await fetchDistrictOptions();
      await fetchUsers();
      successAlert("Name updated", "District name updated successfully.");
    } catch (err) {
      errorAlert("Update failed", err?.message || "Failed to update district name.");
    } finally {
      setRowBusy((prev) => ({ ...prev, [`district-name-${districtId}`]: false }));
    }
  };

  const handleResetDistricts = async () => {
    const { isConfirmed } = await confirmAlert({
      title: "Reset Districts?",
      text: "This will clean current districts and seed the 28 standard districts. Relationships in users, players, and events will be automatically updated and backfilled.",
      confirmText: "Yes, Reset & Seed",
      confirmButtonColor: "#dc2626",
    });

    if (!isConfirmed) return;

    try {
      setLoading(true);
      const res = await API.resetDistricts();
      successAlert("Seeded successfully", res.message || "Districts reset and seeded successfully.");
      await fetchDistrictOptions();
      await fetchUsers();
    } catch (err) {
      errorAlert("Reset failed", err?.message || "Failed to reset districts.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUserPhoto = async () => {
    const { isConfirmed } = await confirmAlert({
      title: "Delete Photo?",
      text: "This will permanently remove the user's profile photo.",
      confirmText: "Delete",
    });
    if (!isConfirmed) return;

    try {
      await API.deleteAdminUserPhoto(editingId);
      setExistingPhoto(null);
      await fetchUsers();
      successAlert("Deleted", "Photo removed successfully.");
    } catch (err) {
      errorAlert("Delete failed", err?.message || "Failed to delete photo.");
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Access Control</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Login Management</h1>
              <p className="text-sm text-slate-600 mt-1">
                {isAdmin
                  ? "Create district/secretary/admin users, block/delete district logins, and maintain logo assets."
                  : "Create district/secretary users, block/delete district logins, and maintain logo assets."}
              </p>
              <button
                type="button"
                onClick={() => {
                  setForm(initialForm);
                  setEditingId(null);
                  setExistingPhoto(null);
                  setIsFormModalOpen(true);
                }}
                className="mt-3 text-white font-semibold py-2 px-4 rounded-xl shadow-sm transition-all flex items-center gap-2 text-xs"
                style={{ backgroundColor: "#0e7490" }}
              >
                <span>➕</span> Create New Login Account
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Users</p>
                <p className="text-lg font-semibold text-slate-900">{users.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Districts</p>
                <p className="text-lg font-semibold text-slate-900">{districtOptions.length}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Editing</p>
                <p className="text-lg font-semibold text-emerald-800">{editingId ? "Yes" : "No"}</p>
              </div>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-cyan-700">Saving</p>
                <p className="text-lg font-semibold text-cyan-800">{loading ? "Yes" : "No"}</p>
              </div>
            </div>
          </div>
        </section>

        <Modal
          isOpen={isFormModalOpen}
          onClose={handleCloseModal}
          title={editingId ? "Edit Login Account" : "Create New Login Account"}
          width="600px"
        >
          <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">Full Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Name"
                className="border p-2 rounded-lg text-sm"
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">Email Address *</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Email"
                className="border p-2 rounded-lg text-sm"
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">Mobile Number</label>
              <input
                name="mobile"
                type="tel"
                value={form.mobile}
                onChange={handleChange}
                placeholder="Mobile"
                className="border p-2 rounded-lg text-sm"
                minLength={10}
                maxLength={15}
              />
            </div>
            {!editingId && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-700">Password</label>
                <input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Leave blank for: welcome@123"
                  className="border p-2 rounded-lg text-sm"
                />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">Role *</label>
              <select
                name="role"
                value={form.role}
                onChange={handleChange}
                className="border p-2 rounded-lg bg-white text-sm"
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-700">District *</label>
              <select
                name="district_id"
                value={form.district_id}
                onChange={handleChange}
                className="border p-2 rounded-lg bg-white text-sm disabled:bg-gray-50 disabled:text-gray-400"
                required={form.role === "district"}
                disabled={form.role !== "district"}
              >
                <option value="">{form.role === "district" ? "Select District" : "Not required for this role"}</option>
                {districtOptions.map((d) => (
                  <option key={`${d.id ?? "na"}-${d.name}`} value={d.id ? `id:${d.id}` : `name:${d.name}`}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            {editingId && existingPhoto && (
              <div className="md:col-span-2 flex items-center gap-3 p-2 bg-gray-50 rounded border border-gray-200">
                <img
                  src={resolveAssetUrl(existingPhoto)}
                  alt="Current"
                  className="w-16 h-16 rounded-full object-cover border"
                />
                <div>
                  <p className="text-xs font-semibold text-gray-700">Current Photo</p>
                  <button
                    type="button"
                    onClick={handleDeleteUserPhoto}
                    className="text-red-650 hover:text-red-850 text-xs font-semibold mt-1"
                  >
                    🗑️ Delete Photo
                  </button>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-1 md:col-span-2">
              <label className="text-xs font-semibold text-gray-700">Profile Photo</label>
              <input
                name="photo"
                type="file"
                accept="image/*"
                onChange={handleChange}
                className="border p-2 rounded-lg text-sm"
              />
            </div>

            <div className="md:col-span-2 flex justify-end gap-3 mt-4 pt-4 border-t">
              <button
                type="button"
                onClick={handleCloseModal}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-all font-semibold text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-semibold transition-all shadow-sm text-sm"
              >
                {loading ? "Saving..." : editingId ? "Update Account" : "Create Account"}
              </button>
            </div>
          </form>
        </Modal>

        <div className="bg-white rounded-2xl border border-slate-200 shadow p-6">
          <h2 className="text-base sm:text-lg font-semibold mb-4">
            {isAdmin ? "All Login Accounts (Users Table)" : "Login Accounts (Users Table)"}
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full border text-xs sm:text-sm">
              <thead className="bg-gray-100 select-none">
                <tr>
                  <th className="p-2 border">S.No.</th>
                  <th className="p-2 border">Photo</th>
                  {[
                    { label: "Name", key: "name" },
                    { label: "Email", key: "email" },
                    { label: "Mobile", key: "mobile" },
                    { label: "District", key: "district" },
                    { label: "Role", key: "role" },
                    { label: "Login Status", key: "is_blocked" },
                    { label: "Created At", key: "created_at" },
                  ].map((col, idx) => (
                    <th
                      key={idx}
                      onClick={() => handleSort(col.key)}
                      className="p-2 border cursor-pointer hover:bg-gray-200 transition"
                    >
                      <div className="flex items-center justify-center gap-1">
                        <span>{col.label}</span>
                        <span className="text-[10px] text-gray-400">
                          {sortConfig.key === col.key
                            ? sortConfig.direction === "asc"
                              ? "▲"
                              : "▼"
                            : "↕"}
                        </span>
                      </div>
                    </th>
                  ))}
                  <th className="p-2 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length ? (
                  sortedUsers.map((u, index) => (
                    <tr key={u.id}>
                      <td className="p-2 border text-center font-semibold">{index + 1}</td>
                      <td className="p-2 border text-center">
                        {String(u.photo || "").trim() ? (
                          <img
                            src={resolveAssetUrl(String(u.photo || "").trim())}
                            alt={u.name}
                            className="w-10 h-10 rounded-full object-cover mx-auto"
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="p-2 border">{u.name}</td>
                      <td className="p-2 border">{u.email}</td>
                      <td className="p-2 border">{u.mobile || "-"}</td>
                      <td className="p-2 border">{u.district || "-"}</td>
                      <td className="p-2 border capitalize">{u.role || "district"}</td>
                      <td className="p-2 border">
                        {String(u.role || "").toLowerCase() === "district" ? (
                          <span className={toBool(u.is_blocked) ? "text-red-700 font-semibold" : "text-emerald-700 font-semibold"}>
                            {toBool(u.is_blocked) ? "Blocked" : "Active"}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="p-2 border">
                        {u.created_at ? new Date(u.created_at).toLocaleString() : "-"}
                      </td>
                      <td className="p-2 border">
                        <div className="flex flex-wrap justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(u)}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded"
                            disabled={!!rowBusy[`block-${u.id}`] || !!rowBusy[`delete-${u.id}`]}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleResetPassword(u)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                            disabled={!!rowBusy[`block-${u.id}`] || !!rowBusy[`delete-${u.id}`]}
                          >
                            Reset Password
                          </button>
                          {String(u.role || "").toLowerCase() === "district" && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleDistrictBlockToggle(u)}
                                className={`text-white px-3 py-1 rounded ${toBool(u.is_blocked) ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} disabled:bg-gray-400`}
                                disabled={!!rowBusy[`block-${u.id}`] || !!rowBusy[`delete-${u.id}`]}
                              >
                                {rowBusy[`block-${u.id}`] ? "Saving..." : (toBool(u.is_blocked) ? "Unblock" : "Block")}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDistrictDelete(u)}
                                className="bg-slate-700 hover:bg-slate-800 text-white px-3 py-1 rounded disabled:bg-gray-400"
                                disabled={!!rowBusy[`block-${u.id}`] || !!rowBusy[`delete-${u.id}`]}
                              >
                                {rowBusy[`delete-${u.id}`] ? "Deleting..." : "Delete"}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="p-3 border text-center text-gray-500" colSpan={9}>
                      No user logins found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow p-6 mt-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-base sm:text-lg font-semibold">District Master List & Logos</h2>
            <button
              type="button"
              onClick={handleResetDistricts}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-xs font-semibold disabled:bg-gray-400"
            >
              🔄 Reset/Seed 28 Districts
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border text-xs sm:text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border">District Name</th>
                  <th className="p-2 border">Current Logo</th>
                  <th className="p-2 border">Upload New Logo</th>
                  <th className="p-2 border">Incharge For</th>
                  <th className="p-2 border">Action</th>
                </tr>
              </thead>
              <tbody>
                {districtOptions.length ? (
                  districtOptions.map((d) => (
                    <tr key={`district-logo-${d.id || d.name}`}>
                      <td className="p-2 border font-medium">
                        <div className="flex items-center justify-between gap-2">
                          <span>{d.name}</span>
                          <button
                            type="button; "
                            onClick={() => handleEditDistrictName(d)}
                            disabled={!!rowBusy[`district-name-${d.id}`]}
                            className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1 font-semibold"
                          >
                            ✏️ Edit Name
                          </button>
                        </div>
                      </td>
                      <td className="p-2 border text-center">
                        {d.logo_path ? (
                          <div className="flex flex-col items-center gap-1">
                            <img
                              src={resolveAssetUrl(d.logo_path, { folder: "districts" })}
                              alt={`${d.name} logo`}
                              className="h-12 object-contain mx-auto"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveDistrictLogo(d)}
                              disabled={!!districtLogoUploading[d.id]}
                              className="text-red-600 hover:text-red-800 text-[11px] font-semibold"
                            >
                              🗑️ Remove Logo
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">No logo</span>
                        )}
                      </td>
                      <td className="p-2 border">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleDistrictLogoFileChange(d.id, e.target.files?.[0] || null)}
                          className="border p-1 rounded w-full text-xs"
                          disabled={!d.id}
                        />
                      </td>
                      <td className="p-2 border min-w-[220px]">
                        <select
                          multiple
                          value={(inchargeAssignments[d.id] || []).map(String)}
                          onChange={(e) => setInchargeAssignments((prev) => ({
                            ...prev,
                            [d.id]: Array.from(e.target.selectedOptions, (option) => Number(option.value)),
                          }))}
                          className="border p-1 rounded w-full text-xs min-h-[80px]"
                        >
                          {districtOptions.filter((option) => option.id !== d.id).map((option) => (
                            <option key={option.id} value={option.id}>{option.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => saveInchargeAssignments(d)}
                          disabled={!!rowBusy[`incharge-${d.id}`]}
                          className="mt-1 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-xs disabled:bg-gray-400"
                        >
                          {rowBusy[`incharge-${d.id}`] ? "Saving..." : "Save incharge"}
                        </button>
                        <p className="mt-1 text-[10px] leading-tight text-amber-700">
                          Selected districts are managed by {d.name}; their direct district logins are disabled.
                        </p>
                      </td>
                      <td className="p-2 border text-center">
                        <button
                          type="button"
                          onClick={() => handleUploadDistrictLogo(d)}
                          disabled={!d.id || !districtLogoFiles[d.id] || !!districtLogoUploading[d.id]}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded text-xs disabled:bg-gray-400 font-semibold"
                        >
                          {districtLogoUploading[d.id] ? "Uploading..." : "Upload"}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="p-3 border text-center text-gray-500" colSpan={5}>
                      No districts found. Click "Reset/Seed 28 Districts" above to initialize.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default DistrictRegistration;
